import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useManualQuery, ClientContext } from "graphql-hooks";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Helmet } from "react-helmet";
import {
  Grid,
  Button,
  Tooltip,
  Typography,
  Checkbox,
  Card,
  TextField,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
  Divider,
  makeStyles,
  createMuiTheme,
  ThemeProvider,
} from "@material-ui/core";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Autocomplete from "@mui/material/Autocomplete";
import Popper from "@material-ui/core/Popper";

import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import draftToHtml from "draftjs-to-html";
import ReactHtmlParser from "react-html-parser";
import styled from "styled-components";

import dropdownImg from "../../assets/icons/dropdown.png";
import infoImg from "../../assets/icons/info.png";
import AlertDialog from "../../components/AlertDialog.js";

import { GET_COMPONENTS_ANSWERS_BY_FORM_ID_QUERY } from "../../graphQL/component_qry";
import {
  GET_FORM_INFO_BY_CLIENT_UUID,
  GET_CLIENT_DATA_BY_ID,
} from "../../graphQL/client_qry";
import { CREATE_PUBLIC_USER_MUTATION } from "../../graphQL/register_qry";
import { CREATE_TERM_APPLICANT_MUTATION } from "../../graphQL/term_applicant_qry";
import { GET_APPLICANT_DATA_BY_USER_EMAIL_QUERY } from "../../graphQL/applicant_qry";

import { project_url } from "../../constants/Data";

var findAnd = require("find-and");

const StyledTooltip = styled(props => (
  <Tooltip
    classes={{ popper: props.className }}
    {...props}
    PopperProps={{
      popperOptions: {
        modifiers: {
          flip: { enabled: false },
          offset: {
            offset: "0px, -50px, 20px, -25px",
          },
        },
      },
    }}
  />
))`
  & .MuiTooltip-tooltip {
    display: flex;
    background: #757575;
    color: #fff;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;
  }
`;

const PopperMy = function (props) {
  return <Popper {...props} style={styles.popper} placement="bottom-start" />;
};

const styles = theme => ({
  popper: {
    width: "fit-content",
  },
});

const formLabelsTheme = createMuiTheme({
  overrides: {
    MuiFormLabel: {
      asterisk: {
        color: "#DB3131",
        "&$error": {
          color: "#DB3131",
        },
      },
    },
  },
});

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: "6rem",
  },
  formContainer: {
    padding: theme.spacing(4),
    // marginTop: "2rem",
    marginBottom: "5rem",
    overflow: "hidden",
    clear: "both",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
  },
  textFieldStyle: {
    marginBottom: 5,
    "& .MuiInputLabel-outlined": {
      fontSize: 16,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: 16,
      backgroundColor: "#E3F2FD",
    },
  },
  radioLabelStyle: {
    "& .MuiTypography-body1": {
      fontSize: 16,
    },
  },
  buttonStyle: {
    borderRadius: 4,
    width: "100px",
    padding: 0,
    color: "#fff",
    height: "34px",
    backgroundColor: "#0D47A1",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#1E88E5",
    },
  },
  autoComStyle: {
    "& .MuiAutocomplete-inputRoot": {
      borderRadius: 10,
      backgroundColor: "#E3F2FD",
    },
  },
  textFieldOnFormStyle: {
    marginBottom: 5,
    "& .MuiInputLabel-outlined": {
      fontSize: 16,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: 16,
      backgroundColor: "#E3F2FD",
    },
  },
  textFieldInput: {
    resize: "both",
    // maxHeight: "120px",
    minHeigth: "75px",
  },
  errorStyle: {
    marginTop: 0,
    fontSize: 14, //16,
    fontFamily: "Arial",
    paddingLeft: 5,
  },
}));

function ClientPublicForm() {
  const { clientUuid } = useParams();
  const classes = useStyles();
  const navigate = useNavigate();
  let tenantName = localStorage.getItem("tenantName")
    ? localStorage.getItem("tenantName")
    : "";

  const [allComponents, setAllComponents] = useState([]);
  const [selectedVal, setSelectedVal] = useState("Select");
  const [loadData, setLoadData] = useState(true);
  const [blankPage, setBlankPage] = useState(false);
  const [formInfo, setFormInfo] = useState([]);
  const [remainedChars, setRemainedChars] = useState([]);
  const [termInfo, setTermInfo] = useState([]);
  const [periodStartDate, setPeriodStartDate] = useState([]);
  const [tenantStates, setTenantStates] = useState([]);
  const [tenantCountries, setTenantCountries] = useState([]);
  const [message, setMessage] = useState("");
  const [clientData, setClientData] = useState([]);
  const [contactEmail, setContactEmail] = useState("");
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  // const [linkedinUrl, setLinkedinUrl] = useState("");

  const {
    register,
    handleSubmit,
    setFocus,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  const linkedinUrl = watch("linkedinUrl");

  const [fetchFormComponentsAnswers] = useManualQuery(
    GET_COMPONENTS_ANSWERS_BY_FORM_ID_QUERY,
    {
      fetchPolicy: "network-only",
    },
  );

  const [getClientData] = useManualQuery(GET_CLIENT_DATA_BY_ID, {
    fetchPolicy: "network-only",
  });

  const [getFormInfo] = useManualQuery(GET_FORM_INFO_BY_CLIENT_UUID, {
    fetchPolicy: "network-only",
  });

  const [getApplicantInfo] = useManualQuery(GET_APPLICANT_DATA_BY_USER_EMAIL_QUERY, {
    fetchPolicy: "network-only",
  });

  const [createPublicUser] = useMutation(CREATE_PUBLIC_USER_MUTATION);
  const [createTermApplicant] = useMutation(CREATE_TERM_APPLICANT_MUTATION);

  const formatter = new Intl.DateTimeFormat("en-us", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  });

  const dayFormat = day => {
    let newFormat = "-";
    if (day) {
      const dayDate = new Date(day);
      const dayFormat = formatter.formatToParts(dayDate);
      newFormat =
        dayFormat.find(a => a.type == "month").value +
        " " +
        dayFormat.find(a => a.type == "day").value +
        ", " +
        dayFormat.find(a => a.type == "year").value;
    }
    return newFormat;
  };

  const getClient = async () => {
    if (clientUuid) {
      // Get terclientm information
      const {
        loading: clientDataLoading,
        error: clientDataError,
        data: clientData,
      } = await getClientData({
        variables: { clientUuid: clientUuid },
      });

      if (!clientDataLoading && clientData && clientData.getClientDataByID) {
        setClientData(clientData.getClientDataByID);
        setContactEmail(clientData.getClientDataByID.tenantId.contactEmail);
      }
      if (
        clientDataError &&
        clientDataError.graphQLErrors &&
        clientDataError.graphQLErrors[0].message === "Signature has expired"
      ) {
        navigate(`${project_url}/signin`);
      }
    }
  };

  const getFormComponents = async () => {
    if (clientUuid) {
      // Get form information
      const {
        loading: formInfoLoading,
        error: formInfoError,
        data: formInfo,
      } = await getFormInfo({
        variables: { clientUuid: clientUuid },
      });

      if (!formInfoLoading && formInfo && formInfo.getFormInfoByClientUuid) {
        setMessage(formInfo.getFormInfoByClientUuid.message);

        if (formInfo.getFormInfoByClientUuid.term) {
          setFormInfo(formInfo.getFormInfoByClientUuid.term.formId);
          setTermInfo(formInfo.getFormInfoByClientUuid.term);
          setTenantStates(
            formInfo.getFormInfoByClientUuid.term.formId.clientId.tenantId.statesList.map(
              ({ name }) => name,
            ),
          );
          setTenantCountries(
            formInfo.getFormInfoByClientUuid.term.formId.clientId.tenantId.countriesList.map(
              ({ name }) => name,
            ),
          );

          localStorage.setItem(
            "tenantName",
            formInfo.getFormInfoByClientUuid.term.formId.clientId.tenantId.name,
          );

          if (formInfo.getFormInfoByClientUuid.period)
            setPeriodStartDate(
              dayFormat(formInfo.getFormInfoByClientUuid.period.startDate),
            );

          if (formInfoError && formInfoError.graphQLErrors) {
            alert(formInfoError.graphQLErrors[0].message);
          }

          if (
            formInfoError &&
            formInfoError.graphQLErrors &&
            formInfoError.graphQLErrors[0].message ===
              "Term matching query does not exist."
          ) {
            alert("Are you sure you have valid URL?");
          }

          if (
            formInfo.getFormInfoByClientUuid.term.urlActivated &&
            formInfo.getFormInfoByClientUuid.form
          ) {
            // Get form components information
            const {
              loading: formComponentsAnswersLoading,
              error: formComponentsAnswersError,
              data: formComponentsAnswersData,
            } = await fetchFormComponentsAnswers({
              variables: {
                formUuid: formInfo.getFormInfoByClientUuid.term.formId.formUuid,
              },
            });

            if (
              !formComponentsAnswersLoading &&
              formComponentsAnswersData &&
              formComponentsAnswersData.formComponentAnswerList
            ) {
              setLoadData(false);
              setAllComponents(
                formComponentsAnswersData.formComponentAnswerList.componentList,
              );
              var remChars = [];
              formComponentsAnswersData.formComponentAnswerList.componentList.map(
                (component, index) => remChars.push({ index: index, charNo: null }),
              );
              setRemainedChars(remChars);
            }

            if (formComponentsAnswersError && formComponentsAnswersError.graphQLErrors) {
              alert(formInfoError.graphQLErrors[0].message);
            }
          } else setBlankPage(true);
        } else setBlankPage(true);
      } else setBlankPage(true);
    } else alert("Are you sure you have valid URL?");
  };

  useEffect(() => {
    (() => getClient())();
  }, []);

  useEffect(() => {
    (() => getFormComponents())();
  }, [clientUuid]);

  const convertTxtToRichTxt = txt => {
    //let contentHTML = draftToHtml(JSON.parse(txt.replace(/'/g, '"')));
    //Commented by Sami 30Aug2025
    let contentHTML = draftToHtml(JSON.parse(txt));
    contentHTML = contentHTML.replace(/<p>/, "");
    contentHTML = contentHTML.replace(
      /<a[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/gi,
      (match, href, text) => {
        // Remove leading protocols
        href = href.replace(/^(https?:\/\/)+/, "");

        // Prepend https://
        href = `https://${href}`;

        return `<a href="${href}" target="_blank">${text}</a>`;
      },
    );
    const convertedTxt = ReactHtmlParser(contentHTML);
    return convertedTxt;
  };

  const updRemainedChars = (input, maxChar, indx) => {
    if (remainedChars) {
      const remCharsList = findAnd.changeProps(
        remainedChars,
        { index: indx },
        {
          charNo: maxChar - input.length,
        },
      );
      setRemainedChars(remCharsList);
    }
  };

  const createAnswersList = formInfo => {
    let answersList = [];
    for (const [key, value] of Object.entries(formInfo)) {
      let foundComponent = allComponents.find(item => item.componentId === key);
      let foundAnswer = {};
      if (foundComponent) {
        if (
          foundComponent.answerTypeId.name === "text" ||
          foundComponent.answerTypeId.name === "textarea" ||
          foundComponent.answerTypeId.name === "checkbox_single"
        ) {
          answersList.push([
            parseInt(foundComponent.answerList[0].answerId),
            formInfo[foundComponent.componentId],
          ]);
        }
        // else if (foundComponent.answerTypeId.name === "checkbox_single"){
        //   answersList.push({
        //     [foundComponent.answerList[0].answerId] : formInfo[foundComponent.componentId]
        //   })
        // }
        else if (foundComponent.answerTypeId.name === "checkbox_multiple") {
          let foundValue = {};
          for (let i = 0; i < value.length; i++) {
            foundValue = foundComponent.answerList.find(
              answer => answer.label === value[i],
            );
            if (foundValue) {
              answersList.push([parseInt(foundValue.answerId), foundValue.label]);
            }
          }
        } else {
          foundAnswer = foundComponent.answerList.find(answer => answer.label === value);
          if (foundAnswer) {
            answersList.push([parseInt(foundAnswer.answerId), foundAnswer.label]);
          }
        }
      }
    }
    return answersList;
  };

  const onSubmit = async formData => {
    const answers = JSON.stringify(createAnswersList(formData)).replace(/"/g, "'");

    // Insert new record in tables "apie_api_user" and "profile"
    if (formData) {
      const { data, error } = await createPublicUser({
        variables: {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNo: formData.phoneNo,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          termId: parseInt(termInfo.termId),
          linkedinUrl: formData.linkedinUrl,
        },
      });
      localStorage.setItem("applicantEmail", formData.email);
      localStorage.setItem("applicantName", formData.firstName + " " + formData.lastName);
      var applicantStatus = "New";
      if (error) {
        if (error.graphQLErrors[0].message == "AdminUser") applicantStatus = "AdminUser";

        if (error.graphQLErrors[0].message == "Returning") applicantStatus = "Returning";
        else if (error.graphQLErrors[0].message == "Duplicate")
          applicantStatus = "Duplicate";
        else if (error.graphQLErrors[0].message == "NewForThisClient")
          applicantStatus = "NewForThisClient";
        else if (error.graphQLErrors[0].message == "Active") applicantStatus = "Active";
        else if (error.graphQLErrors[0].message == "TenantUser")
          applicantStatus = "TenantUser";
        else if (error.graphQLErrors[0].message == "Not-Implemented")
          applicantStatus = "Not-Implemented";
        else if (error.graphQLErrors[0].message == "ApplyNextTerm")
          applicantStatus = "ApplyNextTerm";
      }

      localStorage.setItem("applicantStatus", applicantStatus);
      console.log(applicantStatus);

      // if it is admin user display message
      if (applicantStatus == "AdminUser") {
        setShowAdminDialog(true);
        return; // stop submission
      } else {
        // save form data if applicant is new 
        if (applicantStatus == "New") {
          console.log("Success in adding user record", data.createPublicUser);
          // Sahar RTS-1093
          localStorage.setItem("userRegisterEmail", formData.email);

          // Insert new record in tables "termApplicant"
          const { data: termApplicantData, error: termApplicantError } =
            await createTermApplicant({
              variables: {
                applicantId: parseInt(data.createPublicUser.applicantId),
                termId: parseInt(termInfo.termId),
                applicantStatus: applicantStatus,
                applicationAccepted: false,
                applicationStatus: "Submitted",
                answers: answers.substring(1, answers.length - 1),
              },
            });

          if (termApplicantError) {
            console.log("Error", termApplicantError, termApplicantData);
          } else {
            console.log(
              "Success in adding term applicant record",
              termApplicantData.createTermApplicant,
            );
          }
        }

        // save form data if applicant is returning or apply for next sprintone
        else if (applicantStatus === "Returning" || applicantStatus === "ApplyNextTerm") {
          
          const {
            loading: applicantInfoLoading,
            error: applicantInfoError,
            data: applicantInfo,
          } = await getApplicantInfo({
            variables: { email: formData.email },
          });
          if (
            !applicantInfoLoading &&
            applicantInfo &&
            applicantInfo.getApplicantDataByUserEmail
          ) {
            const { data: termAppData, error: termAppError } = await createTermApplicant({
              variables: {
                applicantId: parseInt(
                  applicantInfo.getApplicantDataByUserEmail.applicantId,
                ),
                termId: parseInt(termInfo.termId),
                applicantStatus: "Returning",
                applicationAccepted: false,
                applicationStatus: "Submitted",
                answers: answers.substring(1, answers.length - 1),
              },
            });

            if (termAppError) {
              console.log("Error", termAppError, termAppData);
            } else {
              console.log(
                "Success in adding term applicant record",
                termAppData.createTermApplicant,
              );
            }
          }
        }

        // save form data if applicant is come from diffrent client or tenant
        else if (applicantStatus == "NewForThisClient") {
          const {
            loading: applicantInfoLoading,
            error: applicantInfoError,
            data: applicantInfo,
          } = await getApplicantInfo({
            variables: { email: formData.email },
          });

          if (
            !applicantInfoLoading &&
            applicantInfo &&
            applicantInfo.getApplicantDataByUserEmail
          ) {
            const { data: termAppData, error: termAppError } = await createTermApplicant({
              variables: {
                applicantId: parseInt(
                  applicantInfo.getApplicantDataByUserEmail.applicantId,
                ),
                termId: parseInt(termInfo.termId),
                applicantStatus: "New",
                applicationAccepted: false,
                applicationStatus: "Submitted",
                answers: answers.substring(1, answers.length - 1),
              },
            });

            if (termAppError) {
              console.log("Error", termAppError, termAppData);
            } else {
              console.log(
                "Success in adding term applicant record",
                termAppData.createTermApplicant,
              );
            }
          }
        }

        navigate(`${project_url}/terms/${termInfo.termUuid}/application-form-submitted/`);
      }
    }
  };

  // const handleLinkedInChange = e => {
  //   setLinkedinUrl(e.target.value);
  // };

  const openLinkedIn = () => {
    if (!linkedinUrl?.trim()) return;

    let url = linkedinUrl.trim();

    if (!/^https?:\/\//i.test(url)) {
      url = `https://${url}`;
    }

    try {
      const parsedUrl = new URL(url);

      if (!parsedUrl.hostname.includes("linkedin.com")) {
        alert("Please enter a valid LinkedIn URL.");
        return;
      }

      window.open(parsedUrl.href, "_blank", "noopener,noreferrer");
    } catch {
      alert("Please enter a valid URL.");
    }
  };

  if (!loadData) {
    return (
      <>
        <Helmet>
          <meta
            http-equiv="Content-Security-Policy"
            content="frame-ancestors 'self' https://sapi24.com https://www.pmoleaders.com;"
          />
          <title>APIE | Application Form</title>
        </Helmet>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={classes.formContainer}>
            <Grid item lg md xl xs>
              <Card>
                <Box
                  pb={0.25}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <Typography
                    variant="h3"
                    style={{ wordBreak: "break-word", paddingLeft: 15, marginBottom: 25 }}
                  >
                    <b>{message}</b>
                  </Typography>
                </Box>
                <Box
                  pb={0.25}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <Typography variant="h3" style={{ paddingLeft: 15, marginBottom: 25 }}>
                    <b>
                      We need to create a profile for you so that you can track the status
                      of your volunteer application.
                    </b>
                  </Typography>
                </Box>
                <Box
                  pb={0.25}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <Typography variant="h3" style={{ paddingLeft: 15 }}>
                    First Name
                  </Typography>
                  <StyledTooltip title="required field">
                    <Typography
                      variant="h3"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        color: "#E53935",
                        paddingLeft: 5,
                        paddingRight: 5,
                      }}
                    >
                      *
                    </Typography>
                  </StyledTooltip>
                </Box>
                <TextField
                  variant="outlined"
                  className={classes.textFieldStyle}
                  inputProps={{
                    maxLength: 30,
                  }}
                  style={{
                    width: 300,
                    marginTop: 15,
                    marginLeft: 50,
                  }}
                  id="firstName"
                  {...register("firstName", { required: true })}
                />
                {errors.firstName && errors.firstName.type === "required" && (
                  <Box ml={6}>
                    <FormHelperText className={classes.errorStyle} error>
                      First name cannot be blank.
                    </FormHelperText>
                  </Box>
                )}

                <Box
                  pb={0.25}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginTop: 45,
                  }}
                >
                  <Typography variant="h3" style={{ paddingLeft: 15 }}>
                    Last Name
                  </Typography>
                  <StyledTooltip title="required field">
                    <Typography
                      variant="h3"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        color: "#E53935",
                        paddingLeft: 5,
                        paddingRight: 5,
                      }}
                    >
                      *
                    </Typography>
                  </StyledTooltip>
                </Box>
                <TextField
                  variant="outlined"
                  className={classes.textFieldStyle}
                  inputProps={{
                    maxLength: 30,
                  }}
                  style={{
                    width: 300,
                    marginTop: 15,
                    marginLeft: 50,
                  }}
                  id="lastName"
                  {...register("lastName", { required: true })}
                />
                {errors.lastName && errors.lastName.type === "required" && (
                  <Box ml={6}>
                    <FormHelperText className={classes.errorStyle} error>
                      Last name cannot be blank.
                    </FormHelperText>
                  </Box>
                )}

                <Box
                  pb={0.25}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginTop: 45,
                  }}
                >
                  <Typography variant="h3" style={{ paddingLeft: 15 }}>
                    Email
                  </Typography>
                  <StyledTooltip title="required field">
                    <Typography
                      variant="h3"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        color: "#E53935",
                        paddingLeft: 5,
                        paddingRight: 5,
                      }}
                    >
                      *
                    </Typography>
                  </StyledTooltip>
                </Box>
                <TextField
                  variant="outlined"
                  className={classes.textFieldStyle}
                  inputProps={{
                    maxLength: 100,
                  }}
                  style={{
                    width: 930,
                    marginTop: 15,
                    marginLeft: 50,
                  }}
                  id="email"
                  {...register("email", {
                    required: true,
                    pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                  })}
                />
                {errors.email && errors.email.type === "required" && (
                  <Box ml={6}>
                    <FormHelperText className={classes.errorStyle} error>
                      Email address cannot be blank.
                    </FormHelperText>
                  </Box>
                )}
                {errors.email && errors.email.type === "pattern" && (
                  <Box ml={6}>
                    <FormHelperText className={classes.errorStyle} error>
                      Enter a correctly-formed email like myname@xyz.com
                    </FormHelperText>
                  </Box>
                )}
                <Typography
                  variant="h3"
                  style={{
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                    marginLeft: 50,
                    marginTop: 5,
                  }}
                >
                  Use this email address at all times when applying for and signing into{" "}
                  {tenantName}
                </Typography>

                <Box
                  pb={0.25}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginTop: 45,
                  }}
                >
                  <Typography variant="h3" style={{ paddingLeft: 15 }}>
                    LinkedIn URL
                  </Typography>
                  <StyledTooltip title="required field">
                    <Typography
                      variant="h3"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        color: "#E53935",
                        paddingLeft: 5,
                        paddingRight: 5,
                      }}
                    >
                      *
                    </Typography>
                  </StyledTooltip>
                </Box>
                <TextField
                  variant="outlined"
                  className={classes.textFieldStyle}
                  inputProps={{
                    maxLength: 150,
                  }}
                  style={{
                    width: 930,
                    marginTop: 15,
                    marginLeft: 50,
                  }}
                  id="linkedinUrl"
                  {...register("linkedinUrl", {
                    required: true,
                    pattern: /^(https?:\/\/)?(www\.)?linkedin\.com(\/.*)?$/i,
                  })}
                  // onChange={handleLinkedInChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={openLinkedIn}
                          disabled={!linkedinUrl || !linkedinUrl.trim()}
                          title={
                            linkedinUrl && linkedinUrl.trim()
                              ? "View LinkedIn page"
                              : "Enter a LinkedIn URL"
                          }
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {errors.linkedinUrl && errors.linkedinUrl.type === "required" && (
                  <Box ml={6}>
                    <FormHelperText className={classes.errorStyle} error>
                      LinkedIn URL cannot be blank.
                    </FormHelperText>
                  </Box>
                )}

                {errors.linkedinUrl && errors.linkedinUrl.type === "pattern" && (
                  <Box ml={6}>
                    <FormHelperText className={classes.errorStyle} error>
                      Please enter a valid LinkedIn URL.
                    </FormHelperText>
                  </Box>
                )}

                <Box
                  pb={0.25}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginTop: 45,
                  }}
                >
                  <Typography variant="h3" style={{ paddingLeft: 15 }}>
                    Phone No
                  </Typography>
                  <StyledTooltip title="required field">
                    <Typography
                      variant="h3"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        color: "#E53935",
                        paddingLeft: 5,
                        paddingRight: 5,
                      }}
                    >
                      *
                    </Typography>
                  </StyledTooltip>
                </Box>
                <TextField
                  variant="outlined"
                  className={classes.textFieldStyle}
                  inputProps={{
                    maxLength: 100,
                  }}
                  style={{
                    width: 300,
                    marginTop: 15,
                    marginLeft: 50,
                  }}
                  id="phoneNo"
                  {...register("phoneNo", { required: true })}
                />
                {errors.phoneNo && errors.phoneNo.type === "required" && (
                  <Box ml={6}>
                    <FormHelperText className={classes.errorStyle} error>
                      Phone No cannot be blank.
                    </FormHelperText>
                  </Box>
                )}
                <Box
                  pb={0.25}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginTop: 45,
                  }}
                >
                  <Typography variant="h3" style={{ paddingLeft: 15 }}>
                    City
                  </Typography>
                  <StyledTooltip title="required field">
                    <Typography
                      variant="h3"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        color: "#E53935",
                        paddingLeft: 5,
                        paddingRight: 5,
                      }}
                    >
                      *
                    </Typography>
                  </StyledTooltip>
                </Box>
                <TextField
                  variant="outlined"
                  className={classes.textFieldStyle}
                  inputProps={{
                    maxLength: 100,
                  }}
                  style={{
                    width: 300,
                    marginTop: 15,
                    marginLeft: 50,
                  }}
                  id="city"
                  {...register("city", { required: true })}
                />
                {errors.city && errors.city.type === "required" && (
                  <Box ml={6}>
                    <FormHelperText className={classes.errorStyle} error>
                      City cannot be blank.
                    </FormHelperText>
                  </Box>
                )}
                <Box
                  pb={0.25}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginTop: 45,
                  }}
                >
                  <Typography variant="h3" style={{ paddingLeft: 15 }}>
                    State
                  </Typography>
                  <StyledTooltip title="required field">
                    <Typography
                      variant="h3"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        color: "#E53935",
                        paddingLeft: 5,
                        paddingRight: 5,
                      }}
                    >
                      *
                    </Typography>
                  </StyledTooltip>
                </Box>
                <form noValidate autoComplete="off">
                  <Autocomplete
                    className={classes.autoComStyle}
                    key={selectedVal}
                    options={tenantStates}
                    PopperComponent={PopperMy}
                    getOptionLabel={option => option}
                    sx={{ width: 400 }}
                    renderInput={params => (
                      <TextField
                        style={{
                          width: 300,
                          marginTop: 15,
                          marginLeft: 50,
                        }}
                        label="Select"
                        margin="normal"
                        name="selectOptions"
                        variant="outlined"
                        {...params}
                        id="state"
                        {...register("state", { required: true })}
                      />
                    )}
                  />
                </form>
                {errors.state && errors.state.type === "required" && (
                  <FormHelperText
                    style={{
                      width: 300,
                      marginLeft: 50,
                    }}
                    className={classes.errorStyle}
                    error
                  >
                    One item must be selected.
                  </FormHelperText>
                )}
                <Box
                  pb={0.25}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginTop: 45,
                  }}
                >
                  <Typography variant="h3" style={{ paddingLeft: 15 }}>
                    Country
                  </Typography>
                  <StyledTooltip title="required field">
                    <Typography
                      variant="h3"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        color: "#E53935",
                        paddingLeft: 5,
                        paddingRight: 5,
                      }}
                    >
                      *
                    </Typography>
                  </StyledTooltip>
                </Box>
                <form noValidate autoComplete="off">
                  <Autocomplete
                    className={classes.autoComStyle}
                    key={selectedVal}
                    options={tenantCountries}
                    PopperComponent={PopperMy}
                    getOptionLabel={option => option}
                    sx={{ width: 400 }}
                    renderInput={params => (
                      <TextField
                        style={{
                          width: 300,
                          marginTop: 15,
                          marginLeft: 50,
                        }}
                        label="Select"
                        margin="normal"
                        name="selectOptions"
                        variant="outlined"
                        {...params}
                        id="country"
                        {...register("country", { required: true })}
                      />
                    )}
                  />
                </form>
                {errors.country && errors.country.type === "required" && (
                  <FormHelperText
                    style={{
                      width: 300,
                      marginLeft: 50,
                    }}
                    className={classes.errorStyle}
                    error
                  >
                    One item must be selected.
                  </FormHelperText>
                )}

                <Divider
                  style={{
                    width: "100%",
                    height: "2px",
                    marginTop: 50,
                    marginBottom: 50,
                  }}
                />
                {allComponents.map((component, index) => (
                  <Box>
                    <List
                      sx={{
                        paddingTop: 0,
                        paddingBottom: 3,
                        "& .MuiListItemButton-root:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      <ListItemButton
                        disableRipple
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          paddingLeft: 10,
                          paddingTop: 5,
                          paddingBottom: 0,
                          cursor: "default",
                        }}
                      >
                        <Box
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            paddingTop: 10,
                          }}
                        >
                          <Box
                            style={{
                              display: "flex",
                              flexDirection: "row",
                            }}
                          >
                            {component.tooltip !== "" &&
                            component.answerTypeId.name !== "content" ? (
                              <StyledTooltip title={component.tooltip}>
                                <img
                                  style={{
                                    paddingBottom: 3,
                                    height: 24,
                                    paddingTop:
                                      component.answerTypeId.name === "checkbox_single" &&
                                      8,
                                  }}
                                  src={infoImg}
                                />
                              </StyledTooltip>
                            ) : (
                              ""
                            )}
                          </Box>
                          <Typography variant="h3">
                            <div
                              style={{
                                wordBreak: "break-word",
                                paddingLeft: 5,
                              }}
                            >
                              {component.answerTypeId.name === "checkbox_single" &&
                                component.answerList.length == 1 &&
                                component.mandatory && (
                                  <>
                                    <span
                                      style={{
                                        color: "#E53935",
                                        verticalAlign: "sub",
                                      }}
                                    >
                                      {" "}
                                      *
                                    </span>
                                    <Checkbox
                                      id={component.componentId}
                                      {...register(`${component.componentId}`, {
                                        required: component.mandatory,
                                      })}
                                    />
                                  </>
                                )}
                              {component.answerTypeId.name === "checkbox_single" &&
                                component.answerList.length == 1 &&
                                !component.mandatory && (
                                  <Checkbox
                                    id={component.componentId}
                                    {...register(`${component.componentId}`, {
                                      required: component.mandatory,
                                    })}
                                  />
                                )}

                              {component.label
                                ? convertTxtToRichTxt(component.label)
                                : ""}
                              {component.answerTypeId.name === "checkbox_single" &&
                                errors[`${component.componentId}`] &&
                                errors[`${component.componentId}`].type ===
                                  "required" && (
                                  <FormHelperText className={classes.errorStyle} error>
                                    This Checkbox should be checked.
                                  </FormHelperText>
                                )}
                            </div>
                          </Typography>
                        </Box>
                      </ListItemButton>

                      <CardContent
                        style={{
                          paddingTop: 5,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-start",
                          marginLeft: 30,
                        }}
                      >
                        {component.caption &&
                          ///JSON.parse(component.caption.replace(/'/g, '"')).blocks[0]
                          //commented by Sami 30Aug2025
                          JSON.parse(component.caption).blocks[0].text !== "" && (
                            <Typography
                              variant="h3"
                              style={{
                                fontSize: 14,
                                display: "flex",
                                alignItems: "center",
                                paddingBottom: 0,
                              }}
                            >
                              <div
                                style={{
                                  textAlign: "left",
                                  wordBreak: "break-word",
                                  paddingLeft: 5,
                                }}
                              >
                                {convertTxtToRichTxt(component.caption)}
                              </div>
                            </Typography>
                          )}
                        {(component.answerTypeId.name === "text" ||
                          component.answerTypeId.name === "textarea") && (
                          <>
                            <Box
                              pb={0.25}
                              style={{
                                display: "flex",
                                flexDirection: "row",
                              }}
                            >
                              <Typography
                                variant="h3"
                                style={{
                                  fontSize: 14,
                                  display: "flex",
                                  alignItems: "center",
                                  paddingLeft: 5,
                                }}
                              >
                                Answer
                              </Typography>
                              {component.mandatory && (
                                <StyledTooltip title="required field">
                                  <Typography
                                    variant="h3"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      color: "#E53935",
                                      paddingLeft: 5,
                                      paddingRight: 5,
                                    }}
                                  >
                                    *
                                  </Typography>
                                </StyledTooltip>
                              )}
                            </Box>
                            {component.answerTypeId.name === "text" && (
                              <TextField
                                id="outlined-multiline-static"
                                variant="outlined"
                                className={classes.textFieldStyle}
                                inputProps={{
                                  maxLength: component.answerList[0].maxLength,
                                }}
                                fullWidth={component.answerList[0].maxLength > 150}
                                style={{
                                  width:
                                    component.answerList[0].maxLength >= 5
                                      ? component.answerList[0].maxLength <= 150
                                        ? component.answerList[0].maxLength * 10
                                        : null
                                      : 80,
                                }}
                                id={component.componentId}
                                {...register(`${component.componentId}`, {
                                  required: component.mandatory,
                                })}
                                onChange={e => {
                                  updRemainedChars(
                                    e.target.value,
                                    component.answerList[0].maxLength,
                                    index,
                                  );
                                }}
                              />
                            )}
                            {component.answerTypeId.name === "text" &&
                              errors[`${component.componentId}`] &&
                              errors[`${component.componentId}`].type === "required" && (
                                <FormHelperText className={classes.errorStyle} error>
                                  This field cannot be blank.
                                </FormHelperText>
                              )}
                            {component.answerTypeId.name === "textarea" && (
                              <Box style={{ width: "fit-content" }}>
                                <TextField
                                  id="outlined-multiline-static"
                                  variant="outlined"
                                  multiline
                                  inputProps={{
                                    maxLength: component.answerList[0].maxLength,
                                  }}
                                  style={{
                                    minWidth: component.answerList[0].width * 10,
                                    maxWidth: "1350px",
                                  }}
                                  rows={component.answerList[0].height}
                                  className={classes.textFieldOnFormStyle}
                                  InputProps={{
                                    classes: {
                                      input: classes.textFieldInput,
                                    },
                                  }}
                                  id={component.componentId}
                                  {...register(`${component.componentId}`, {
                                    required: component.mandatory,
                                  })}
                                  onChange={e => {
                                    updRemainedChars(
                                      e.target.value,
                                      component.answerList[0].maxLength,
                                      index,
                                    );
                                  }}
                                />
                              </Box>
                            )}
                            {component.answerTypeId.name === "textarea" &&
                              errors[`${component.componentId}`] &&
                              errors[`${component.componentId}`].type === "required" && (
                                <FormHelperText className={classes.errorStyle} error>
                                  This field cannot be blank.
                                </FormHelperText>
                              )}
                            <Typography
                              variant="h6"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                paddingRight: 5,
                                paddingTop: 5,
                                color: "#00000099",
                              }}
                            >
                              {component.answerList &&
                              component.answerList[0] &&
                              component.answerList[0].maxLength
                                ? remainedChars.length &&
                                  remainedChars[index].charNo !== null
                                  ? remainedChars[index].charNo +
                                    " of " +
                                    component.answerList[0].maxLength +
                                    " Character(s) left"
                                  : component.answerList[0].maxLength +
                                    " of " +
                                    component.answerList[0].maxLength +
                                    " Character(s) left"
                                : ""}
                            </Typography>
                          </>
                        )}
                        {component.answerTypeId.name === "drop_down" && (
                          <ThemeProvider theme={formLabelsTheme}>
                            <form noValidate autoComplete="off">
                              <Autocomplete
                                className={classes.autoComStyle}
                                key={selectedVal}
                                options={component.answerList.map(({ label }) => label)}
                                // disableClearable
                                PopperComponent={PopperMy}
                                getOptionLabel={option => option}
                                sx={{ width: 400 }}
                                renderInput={params => (
                                  <TextField
                                    required={component.mandatory}
                                    label={
                                      selectedVal === "Select"
                                        ? "Select"
                                        : selectedVal === "Use State"
                                          ? "Select State"
                                          : "Select Country"
                                    }
                                    margin="normal"
                                    name="selectOptions"
                                    variant="outlined"
                                    {...params}
                                    id={component.componentId}
                                    {...register(`${component.componentId}`, {
                                      required: component.mandatory,
                                    })}
                                  />
                                )}
                              />
                            </form>
                          </ThemeProvider>
                        )}
                        {component.answerTypeId.name === "drop_down" &&
                          errors[`${component.componentId}`] &&
                          errors[`${component.componentId}`].type === "required" && (
                            <FormHelperText className={classes.errorStyle} error>
                              One item must be selected.
                            </FormHelperText>
                          )}
                        {component.answerTypeId.name === "radio_button" && (
                          <>
                            {component.mandatory && (
                              <Box
                                pb={0.25}
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                }}
                              >
                                <Typography
                                  variant="h3"
                                  style={{
                                    fontSize: 14,
                                    display: "flex",
                                    alignItems: "center",
                                    paddingLeft: 5,
                                  }}
                                >
                                  Required
                                </Typography>
                                <StyledTooltip title="required field">
                                  <Typography
                                    variant="h3"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      color: "#E53935",
                                      paddingLeft: 5,
                                      paddingRight: 5,
                                    }}
                                  >
                                    *
                                  </Typography>
                                </StyledTooltip>
                              </Box>
                            )}
                            <RadioGroup
                              aria-labelledby="demo-radio-buttons-group-label"
                              name="radio-buttons-group"
                            >
                              {component.answerList &&
                                component.answerList
                                  .map(({ label }) => label)
                                  .map((option, index) => (
                                    <FormControlLabel
                                      value={option}
                                      control={<Radio />}
                                      label={option}
                                      className={classes.radioLabelStyle}
                                      id={component.componentId}
                                      {...register(`${component.componentId}`, {
                                        required: component.mandatory,
                                      })}
                                    />
                                  ))}
                              {component.answerTypeId.name === "radio_button" &&
                                errors[`${component.componentId}`] &&
                                errors[`${component.componentId}`].type ===
                                  "required" && (
                                  <FormHelperText className={classes.errorStyle} error>
                                    At least one item should be selected.
                                  </FormHelperText>
                                )}
                            </RadioGroup>
                          </>
                        )}

                        {component.answerTypeId.name === "checkbox_multiple" && (
                          <>
                            {component.answerList.length > 0 && component.mandatory && (
                              <Box
                                pb={0.25}
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                }}
                              >
                                <Typography
                                  variant="h3"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    paddingLeft: 5,
                                    paddingRight: 5,
                                    fontSize: 14,
                                  }}
                                >
                                  <div>
                                    Required <span style={{ color: "#E53935" }}> *</span>
                                  </div>
                                </Typography>
                              </Box>
                            )}
                            <Box
                              style={{
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              {component.answerList
                                .map(({ label }) => label)
                                .map((element, index) => (
                                  <FormControlLabel
                                    control={<Checkbox />}
                                    label={element}
                                    value={element}
                                    id={component.componentId}
                                    {...register(`${component.componentId}`, {
                                      required: component.mandatory,
                                    })}
                                  />
                                ))}
                              {component.answerTypeId.name === "checkbox_multiple" &&
                                errors[`${component.componentId}`] &&
                                errors[`${component.componentId}`].type ===
                                  "required" && (
                                  <FormHelperText className={classes.errorStyle} error>
                                    At least one item should be selected.
                                  </FormHelperText>
                                )}
                            </Box>
                          </>
                        )}
                      </CardContent>
                    </List>
                  </Box>
                ))}
                <Box
                  mt={5}
                  ml={2}
                  //  mb={3}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.buttonStyle}
                    style={{ marginRight: 2 }}
                    type="submit"
                  >
                    Submit
                  </Button>
                </Box>
              </Card>
            </Grid>
          </div>

          {showAdminDialog && (
            <AlertDialog
              title="Invalid Email"
              contentMsg='The email address you entered is not allowed. Please enter another email address and click the "Submit" button again.'
              closeDialog={() => {
                setShowAdminDialog(false);

                // Focus email field AFTER dialog closes
                setTimeout(() => {
                  setFocus("email");
                }, 0);
              }}
            />
          )}
        </form>
      </>
    );
  } else if (blankPage) {
    return (
      <>
        <Helmet>
          <meta
            http-equiv="Content-Security-Policy"
            content="frame-ancestors 'self' https://sapi24.com https://www.pmoleaders.com;"
          />
          <title>APIE | Application Form</title>
        </Helmet>
        <div className={classes.root}>
          <Grid item lg md xl xs>
            <Card>
              <Box
                pb={0.25}
                style={{
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                {message !== "" ? (
                  <Typography variant="h3" style={{ paddingLeft: 15, marginBottom: 25 }}>
                    <b>
                      We are not currently accepting volunteer applications for "
                      {termInfo.name}".
                      <br />
                      Please come back on {periodStartDate} to submit your application.
                      <br />
                      If you have any question about this, please contact
                      <b>
                        {" "}
                        <a href={`mailto:${contactEmail}`}>{contactEmail}</a>{" "}
                      </b>
                      .
                    </b>
                  </Typography>
                ) : (
                  <>
                    <Typography
                      variant="h3"
                      style={{ paddingLeft: 15, marginBottom: 25 }}
                    >
                      <b>
                        We are not currently accepting volunteer applications.
                        <br />
                        Please come back when we are open to accept applications for the
                        new term.
                        <br />
                        If you have any question about this, please contact
                        <b>
                          {" "}
                          <a href={`mailto:${contactEmail}`}>{contactEmail}</a>{" "}
                        </b>
                        .
                      </b>
                    </Typography>
                  </>
                )}
              </Box>
            </Card>
          </Grid>
        </div>
      </>
    );
  }
}

export default ClientPublicForm;
